export class LwApiPM {
    static { this.CMD_ID = 0; }
    static { this.LISTENER_ID = -1; }
    constructor(win) {
        this.win = win;
        this.commands = {};
        this.selectionListenerId = -1;
        this.visibilityListenerId = -1;
        this.readyListenerId = -1;
        this.sceneChangeListenerId = -1;
        this.errorListenerId = -1;
        this.selectionCallbacks = {};
        this.visibilityCallbacks = {};
        this.readyCallbacks = {};
        this.sceneChangeCallbacks = {};
        this.contextMenuItemCallbacks = {};
        this.errorCallbacks = {};
        this.handler = (_event) => {
            if (_event && _event.data) {
                if (_event.data.id) {
                    this.receiveResult(_event.data);
                }
                else if (_event.data.type) {
                    const { type, event } = _event.data;
                    if (type === 'onSelectionChanged') {
                        this.onSelectionChanged(event);
                    }
                    else if (type === 'onVisibilityChanged') {
                        this.onVisibilityChanged(event);
                    }
                    else if (type === 'onReady') {
                        this.onReady();
                    }
                    else if (type === 'onSceneChange') {
                        this.onSceneChange();
                    }
                    else if (type === 'onContextMenuItem') {
                        this.onContextMenuItem(event);
                    }
                    else if (type === 'onError') {
                        this.onError(event);
                    }
                    else {
                        console.error('Unknown event data:', event.data);
                    }
                }
            }
        };
        window.addEventListener('message', this.handler);
        // this.win.onunload = () => {
        //   console.log('removing message handler');
        //   window.removeEventListener('message', this.handler);
        // };
    }
    dispose() {
        window.removeEventListener('message', this.handler);
    }
    sendCommand(..._args) {
        const id = ++LwApiPM.CMD_ID;
        const cmd = _args[0] || '';
        const args = _args.slice(1);
        const command = { id, cmd, args };
        return new Promise((resolve, reject) => {
            this.commands[id] = { resolve, reject };
            this.win.postMessage(command, '*');
        });
    }
    receiveResult(_result) {
        const { id, result = undefined, error = undefined } = _result;
        const { resolve, reject } = this.commands[id];
        delete this.commands[id];
        if (error) {
            reject(error);
        }
        else {
            resolve(result);
        }
    }
    onSelectionChanged(event) {
        for (let k in this.selectionCallbacks) {
            if (this.selectionCallbacks.hasOwnProperty(k)) {
                this.selectionCallbacks[k](event);
            }
        }
    }
    onVisibilityChanged(event) {
        for (let k in this.visibilityCallbacks) {
            if (this.visibilityCallbacks.hasOwnProperty(k)) {
                this.visibilityCallbacks[k](event);
            }
        }
    }
    onReady() {
        for (let k in this.readyCallbacks) {
            if (this.readyCallbacks.hasOwnProperty(k)) {
                this.readyCallbacks[k]();
            }
        }
    }
    onError(event) {
        for (let k in this.errorCallbacks) {
            if (this.errorCallbacks.hasOwnProperty(k)) {
                this.errorCallbacks[k](event);
            }
        }
    }
    onSceneChange() {
        for (let k in this.sceneChangeCallbacks) {
            if (this.sceneChangeCallbacks.hasOwnProperty(k)) {
                this.sceneChangeCallbacks[k]();
            }
        }
    }
    onContextMenuItem(event) {
        this.contextMenuItemCallbacks[event.text](event.pt2);
    }
    setOrigin(origin) {
        return this.sendCommand('setOrigin', origin);
    }
    /**
     * Open the given model.
     * @param url Location of the model file. Give an absolute file path on the webviewer server, or a URL.
     * @param config Custom HTTP headers for requesting model data over HTTP/HTTPS.
     * @returns The closeModel() method returns an object. If the command has succeeded, msg is an empty string and error is null. If an error has occurred, msg contains the error message and error is an error object.
     */
    openModel(url, config) {
        return this.sendCommand('openModel', url, config);
    }
    /**
     * Display a TIFF image given as base64 string.
     * Images and 3D models cannot be displayed together in the viewing area. The image is closed when you open or add another file.
     *  This function is intended for TIFF images with 1-bit color depth (black and white) and CCITT Group 4 compression.
     * In multi-page TIFF files, only the first page is displayed.
     * @param data TIFF image data as base64-encoded string.
     * @returns
     */
    openImageFromBase64(data) {
        return this.sendCommand('openImageFromBase64', data);
    }
    /**
     * Add the given model to the scene. Optionally, a transformation can be applied to the model. Note that you cannot open the same model twice.
     * @param url Location of the model file. Give a full file path on the webviewer server, or a URL.
     * @param transformation 4x4 transformation matrix in column-major order. Omit this parameter to apply no transformation.
     * @param name Name of the model to display in the tree. The default value is the file name.
     * @param config Custom headers for requesting model data over HTTP/HTTPS.
     * @returns The closeModel() method returns an object. If the command has succeeded, msg is an empty string and error is null. If an error has occurred, msg contains the error message and error is an error object.
     */
    addModel(url, transformation, name, config) {
        return this.sendCommand('addModel', url, transformation, name, config);
    }
    /**
     * Close the given model.
     * @param url Location of the model file. Give the full file path on the webviewer server, or a URL.
     * @returns The closeModel() method returns an object. If the command has succeeded, msg is an empty string and error is null. If an error has occurred, msg contains the error message and error is an error object.
     */
    closeModel(url) {
        return this.sendCommand('closeModel', url);
    }
    addTreeData(data) {
        return this.sendCommand('addTreeData', data);
    }
    /**
     * Get tree data for the given model or all models.
     * @param url Location of the model file, which can be a URL or absolute file path. This parameter is optional.
     * @returns If you leave out the url parameter, getTree returns a Promise with an array of objects, one LwTreeData object for each model. If you specify a model with the url parameter, the returned value is a single LwTreeData object for that model.
     */
    getTree(url) {
        return this.sendCommand('getTree', url);
    }
    /**
     * Get the current state of the model tree
     * @param url Capture the tree state for the model with the given URL only. Leave out the URL to capture the tree state for the entire scene.
     * @returns An Object containing information about visibility of parts and assemblies, expanded and collapsed tree branches and item selection, if any.
     */
    getTreeState(url) {
        return this.sendCommand('getTreeState', url);
    }
    /**
     * Set the model tree to a defined state which has been captured before using getTreeState().
     * @param tree Captured model tree state
     * @returns
     */
    setTreeState(tree) {
        return this.sendCommand('setTreeState', tree);
    }
    /**
     * Search for an item with the given ID in tree data.
     * @param id Item ID of the format model:item, for example "1:42"
     * @param selector Item selector. If the selected item does not match the type selector, searchTreeItem returns an error message. To accept any item, use an empty object {} as item selector.
     * @returns an object of the LwTreeDataItem type
     */
    searchTreeItem(id, selector) {
        return this.sendCommand('searchTreeItem', id, selector);
    }
    /**
     * Toggle the visibility of JT subnode items in the tree.
     * @param enable Set true to show the subnodes or false to hide them
     * @returns
     */
    subnodeVisibility(enable) {
        return this.sendCommand('subnodeVisibility', enable);
    }
    /**
     * Get the properties of an item with the given ID.
     * @param id Item ID in the form model:item, for example "1:42"
     * @returns Properties of the item
     */
    getProperties(id) {
        return this.sendCommand('getProperties', id);
    }
    /**
     * Toggle the selection of the given item, or deselect all items.
     * @param id Item ID in the form model:item, for example "1:42"
     * @param state To toggle the selection of the given item, set true. To deselect all, set false.
     * @returns The result value is always 0.
     */
    setNodeSelection(id, state) {
        return this.sendCommand('setNodeSelection', id, state);
    }
    /**
     * Show or hide the given item.
     * @param id Item ID in the form model:item, for example "1:321"
     * @param state To show the item, set true. To hide the item, set false.
     * @returns The result value is always 0.
     */
    setNodeVisibility(id, state) {
        return this.sendCommand('setNodeVisibility', id, state);
    }
    /**
     * Make the given item visible, and hide all other items.
     * @param id Item ID in the form model:item, for example "1:456"
     * @returns The result value is always 0.
     */
    isolate(id) {
        return this.sendCommand('isolate', id);
    }
    /**
     * Reframe the scene, optionally to a given item.
     * @param id Reframe to the item with the given ID. Leave out the ID to fit the entire scene into view.
     * @returns The result value is always 0.
     */
    reframe(id) {
        return this.sendCommand('reframe', id);
    }
    /**
     * Enable or disable the measurement tool.
     * @returns
     */
    toggleMeasurementMode() {
        return this.sendCommand('toggleMeasurementMode');
    }
    /**
     * Manually force the viewer element to resize.
     * @returns
     */
    triggerResizeEvent() {
        return this.sendCommand('triggerResizeEvent');
    }
    /**
     * Set the projection mode of the camera.
     * @param state Set 0 for orthogonal projection, or 1 for parallel projection.
     * @returns
     */
    setProjection(state) {
        return this.sendCommand('setProjection', state);
    }
    /**
     * Take a screenshot of the scene. The function returns base64-encoded image data, or prompts the user to save the screenshot to a file.
     * @param options Screenshot options
     * @returns
     */
    screenshot(options = {}) {
        return this.sendCommand('screenshot', options);
    }
    /**
     * Show or hide the model bounding box.
     * @param state Set true to show the bounding box, or false to hide it.
     * @returns
     */
    setBoundingBoxVisibility(state) {
        return this.sendCommand('setBoundingBoxVisibility', state);
    }
    /**
     * Show or hide PMI elements.
     * @param state Set true to show PMI elements, or false to hide them.
     * @returns
     */
    setPmiVisibility(state) {
        return this.sendCommand('setPmiVisibility', state);
    }
    /**
     * Set the rotation around a given axis.
     * @param rotation Rotation properties to be set.
     * @returns
     */
    setRotation(rotation) {
        return this.sendCommand('setRotation', rotation);
    }
    /**
     * Apply a color and transparency to a part or assembly.
     * @param id Item ID of the format model:item, for example "1:23".
     * @param color RGB color and transparency value. Specify the RGBA code as a list of four numbers 0 to 255.
     * @returns
     */
    setColor(id, color) {
        return this.sendCommand('setColor', id, color);
    }
    /**
     * Reset the color of a given part, assembly, or the entire model.
     * @param id Item ID of the format model:item, for example "1:42". Omit this parameter to reset the color of all items.
     * @returns
     */
    resetColor(id) {
        return this.sendCommand('resetColor', id);
    }
    /**
     * Set the camera to the given standard view.
     * @param view The standard view to set.
     * @returns
     */
    setView(view) {
        return this.sendCommand('setView', view);
    }
    /**
     * Get the GUI configuration that is currently set in the viewer.
     * @returns The current GUI configuration.
     */
    getConfig() {
        return this.sendCommand('getConfig');
    }
    /**
     * Apply a configuration during runtime.
     * @param newConfig Object with one or more key-value pairs.
     * @returns
     */
    applyConfig(newConfig) {
        return this.sendCommand('applyConfig', newConfig);
    }
    /**
     * Get the default GUI configuration.
     * @returns The default GUI configuration.
     */
    getEmptyConfig() {
        return this.sendCommand('getEmptyConfig');
    }
    /**
     * Get the model views attached to the root element, or the model views attached to the item defined by the ID.
     * @param id Leave out the ID to get the model views attached to the root assembly. Set an ID to get  the model views attached to the corresponding item, for example "1:25". Non-root model views are only available if nonRootModelViews config option is set to true.
     * @returns An array of LwModelViewData objects.
     */
    getModelViews(id) {
        return this.sendCommand('getModelViews', id);
    }
    /**
     * Set the given model view.
     * @param index Index number of the model view as returned by getModelViews.
     * @param id ID of the item to which the model views are attached. If nonRootModelViews config is set to false, you can omit the ID.
     * @returns
     */
    setModelView(index, id) {
        return this.sendCommand('setModelView', index, id);
    }
    /**
     * Get a URL to a document that contains all of our used OSS licenses.
     * @returns A URL to a PDF
     */
    getLicenseUrl() {
        return this.sendCommand('getLicenseUrl');
    }
    /**
     * Get a URL to the documentation.
     * @returns A URL to the documentation
     */
    getHelpUrl() {
        return this.sendCommand('getHelpUrl');
    }
    showImage(url, urlHighlight) {
        return this.sendCommand('showImage', url, urlHighlight);
    }
    closeImage() {
        return this.sendCommand('closeImage');
    }
    /**
     * Highlight a square region of the displayed image.
     * Highlighting is intended for TIFF images with 1-bit color depth (black and white) and CCITT Group 4 compression.
     * Highlighting is not supported for other sub-formats.
     * The highlighting color is defined in the configuration by highlightColor.
     * @param x X coordinate [pixel] of the center of the square.
     * @param y Y coordinate [pixel] of the center of the square.
     * @param d Side length [pixel] of the square.
     * @returns
     */
    highlightImage(x, y, d) {
        return this.sendCommand('highlightImage', x, y, d);
    }
    /**
     * Zoom to the given square region of an image.
     * @param x X coordinate [pixel] of the center of the square zoom region.
     * @param y Y coordinate [pixel] of the center of the square zoom region.
     * @param d Side length [pixel] of the square zoom region.
     * @returns
     */
    fitInImage(x, y, d) {
        return this.sendCommand('fitInImage', x, y, d);
    }
    /**
     * Highlight a given PMI item defined by its text-origin property.
     * The numbers in the textOrigin property do not need to match exactly. Rounding errors are tolerated.
     * @param x X position [millimeters] of the text origin.
     * @param y Y position [millimeters] of the text origin.
     * @param z Z position [millimeters] of the text origin.
     * @returns
     */
    highlightByOrigin(x, y, z) {
        return this.sendCommand('highlightByOrigin', x, y, z);
    }
    /**
     * Fit a given PMI item to the viewing area. The PMI item is given by its textOrigin property.
     * The numbers in the textOrigin property do not need to match exactly. Rounding errors are tolerated.
     * @param x X position [millimeters] of the text origin.
     * @param y Y position [millimeters] of the text origin.
     * @param z Z position [millimeters] of the text origin.
     * @returns
     */
    fitInByOrigin(x, y, z) {
        return this.sendCommand('fitInByOrigin', x, y, z);
    }
    /**
     * Add points to the viewing area.
     * Before you can add points, open or add at least one model.
     * @param positions Array of coordinates of the points to add.
     * @param r Color of the points to add. Specify the RGB color code as a list of three numbers 0.0 to 1.0.
     * @param g Color of the points to add. Specify the RGB color code as a list of three numbers 0.0 to 1.0.
     * @param b Color of the points to add. Specify the RGB color code as a list of three numbers 0.0 to 1.0.
     * @param radius Point radius [mm].
     * @param ids Array of IDs. Every point must have an ID. The ID must not contain a colon ':'
     * @param modelUrl URL of the model to which the points are applied. This parameter is optional. If you omit this parameter, the points are applied to the model added last to the viewing area.
     * @returns
     */
    addPoints(positions, r, g, b, radius, ids, modelUrl) {
        return this.sendCommand('addPoints', positions, r, g, b, radius, ids, modelUrl);
    }
    /**
     * This function returns an object defining the current camera position. In addition, the object provides a screenshot as base64-encoded string, and an array of points that were added with addPoints.
     * @returns
     */
    getCaptureWithPoints() {
        return this.sendCommand('getCaptureWithPoints');
    }
    /**
     * This function gets the current camera view. The camera view can stored in a variable and applied again later with setCamera.
     * @returns A camera snapshot object.
     */
    getCamera() {
        return this.sendCommand('getCamera');
    }
    /**
     * This function sets the camera to a defined view which has been captured using getCamera or getCaptureWithPoints.
     * @param camera LwCameraSnapshot object created using getCamera or getCaptureWithPoints
     * @returns
     */
    setCamera(camera) {
        return this.sendCommand('setCamera', camera);
    }
    /**
     * Attach a comment to a 3D point. The comment is shown as a clickable symbol.
     * @param id Identifier of the comment
     * @param position Coordinates of the 3D point to which the comment is attached
     * @returns
     */
    addComment(id, position) {
        return this.sendCommand('addComment', id, position);
    }
    /**
     * Remove a comment with a given identifier.
     * @param id Identifier of the comment to remove.
     * @returns
     */
    removeComment(id) {
        return this.sendCommand('removeComment', id);
    }
    /**
     * Remove all comments.
     * @returns
     */
    removeAllComments() {
        return this.sendCommand('removeAllComments');
    }
    /**
     * Hide or show comments given by a list of identifiers.
     * @param show Show (true) or hide (false) the comments on the list.
     * @param list List of identifiers of the comments to show or hide. An empty list [] stands for all comments.
     * @param reset If this parameter is false or not given, apply the given visibility to the comments on the list, and leave all others unchanged. If this parameter is true, apply the given visibility to the comments on the list, and the opposite visibility to all other comments.
     * @returns
     */
    showHideComments(show, list, reset = false) {
        return this.sendCommand('showHideComments', show, list, reset);
    }
    /**
     * Set a custom image as the clickable symbol for comments. Set the image once per session and before adding any comments. For best appearance, use an image with equal width and height.
     * @param img base64-encoded image data in PNG format.
     * @param width Width of the image in pixels. Use the native width of the image.
     * @returns
     */
    setCommentImage(img, width) {
        return this.sendCommand('setCommentImage', img, width);
    }
    /**
     * Opens the about dialog.
     * @returns
     */
    about() {
        return this.sendCommand('about');
    }
    /**
     * Register a callback function for a selection event in the tree or scene.
     * @param callback Execute this function on the selection event.
     * @returns ID of the listener which is required to unregister the callback
     */
    registerOnSelection(callback) {
        if (typeof callback !== 'function') {
            return Promise.reject('Callback function required');
        }
        const id = ++LwApiPM.LISTENER_ID;
        let promise;
        if (Object.keys(this.selectionCallbacks).length === 0) {
            promise = this.sendCommand('registerOnSelection').then((listenerId) => {
                this.selectionListenerId = listenerId;
                return id;
            });
        }
        else {
            promise = Promise.resolve(id);
        }
        this.selectionCallbacks[id] = callback;
        return promise;
    }
    /**
     * Unregister a selection event listener.
     * @param listenerId ID of the selection event listener to unregister.
     * @returns
     */
    unregisterOnSelection(listenerId) {
        delete this.selectionCallbacks[listenerId];
        let promise;
        if (Object.keys(this.selectionCallbacks).length === 0) {
            promise = this.sendCommand('unregisterOnSelection', this.selectionListenerId).then(() => {
                this.selectionListenerId = -1;
                return null;
            });
        }
        else {
            promise = Promise.resolve(null);
        }
        return promise;
    }
    /**
     * Register a callback function for the event of an item changing its visibility.
     * @param callback Execute this function on the visibility change event.
     * @returns The returned number is the id of the listener which is required to unregister the callback.
     */
    registerOnVisibility(callback) {
        if (typeof callback !== 'function') {
            return Promise.reject('Callback function required');
        }
        const id = ++LwApiPM.LISTENER_ID;
        let promise;
        if (Object.keys(this.visibilityCallbacks).length === 0) {
            promise = this.sendCommand('registerOnVisibility').then((listenerId) => {
                this.visibilityListenerId = listenerId;
                return id;
            });
        }
        else {
            promise = Promise.resolve(id);
        }
        this.visibilityCallbacks[id] = callback;
        return promise;
    }
    /**
     * Unregister a visibility event listener.
     * @param listenerId ID of the visibility event listener to unregister.
     * @returns
     */
    unregisterOnVisibility(listenerId) {
        delete this.visibilityCallbacks[listenerId];
        let promise;
        if (Object.keys(this.visibilityCallbacks).length === 0) {
            promise = this.sendCommand('unregisterOnVisibility', this.visibilityListenerId).then(() => {
                this.visibilityListenerId = -1;
                return null;
            });
        }
        else {
            promise = Promise.resolve(null);
        }
        return promise;
    }
    /**
     * Register an event handler for the event when model data has been loaded completely.
     * The event occurs as openModel or openImageFromBase64 becomes resolved. The event also occurs immediately when registering the event handler if a model is already open.
     * On this event, all data has been loaded but visualization might not be complete. The client still takes some time to visualize the model. The time can vary depending on model size, client hardware, and browser.
     * @param callback Execute this function when the model has been loaded.
     * @returns The returned number is the id of the listener which is required to unregister the callback.
     */
    registerOnReady(callback) {
        if (typeof callback !== 'function') {
            return Promise.reject(-1);
        }
        const id = ++LwApiPM.LISTENER_ID;
        let promise;
        if (Object.keys(this.readyCallbacks).length === 0) {
            promise = this.sendCommand('registerOnReady').then((listenerId) => {
                this.readyListenerId = listenerId;
                return id;
            });
        }
        else {
            promise = Promise.resolve(id);
        }
        this.readyCallbacks[id] = callback;
        return promise;
    }
    /**
     * Unregister the callback function defined by registerOnReady.
     * @param listenerId ID of the event listener to unregister.
     * @returns
     */
    unregisterOnReady(listenerId) {
        delete this.readyCallbacks[listenerId];
        let promise;
        if (Object.keys(this.readyCallbacks).length === 0) {
            promise = this.sendCommand('unregisterOnReady', this.readyListenerId).then(() => {
                this.readyListenerId = -1;
                return null;
            });
        }
        else {
            promise = Promise.resolve(null);
        }
        return promise;
    }
    /**
     * Register a callback function listening for error events. The callback function receives an error code and message.
     * @param callback Execute this function when an error has occured.
     * @returns The returned number is the id of the listener which is required to unregister the callback.
     */
    registerOnError(callback) {
        if (typeof callback !== 'function') {
            return Promise.reject(-1);
        }
        const id = ++LwApiPM.LISTENER_ID;
        let promise;
        if (Object.keys(this.errorCallbacks).length === 0) {
            promise = this.sendCommand('registerOnError').then((listenerId) => {
                this.errorListenerId = listenerId;
                return id;
            });
        }
        else {
            promise = Promise.resolve(id);
        }
        this.errorCallbacks[id] = callback;
        return promise;
    }
    /**
     * Unregister a custom error handler function.
     * @param listenerId ID of the error event listener to unregister.
     * @returns
     */
    unregisterOnError(listenerId) {
        delete this.errorCallbacks[listenerId];
        let promise;
        if (Object.keys(this.errorCallbacks).length === 0) {
            promise = this.sendCommand('unregisterOnError', this.errorListenerId).then(() => {
                this.errorListenerId = -1;
                return null;
            });
        }
        else {
            promise = Promise.resolve(null);
        }
        return promise;
    }
    /**
     * Register an event handler for the event when the camera changes (rotate, zoom, pan, change the projection mode, resize the browser window).
     * @param callback Execute this function when the model has been loaded.
     * @returns The returned number is the id of the listener which is required to unregister the callback.
     */
    registerOnSceneChange(callback) {
        if (typeof callback !== 'function') {
            return Promise.reject(-1);
        }
        const id = ++LwApiPM.LISTENER_ID;
        let promise;
        if (Object.keys(this.sceneChangeCallbacks).length === 0) {
            promise = this.sendCommand('registerOnSceneChange').then((listenerId) => {
                this.sceneChangeListenerId = listenerId;
                return id;
            });
        }
        else {
            promise = Promise.resolve(id);
        }
        this.sceneChangeCallbacks[id] = callback;
        return promise;
    }
    /**
     * Unregister the callback function defined by registerOnSceneChange.
     * @param listenerId ID of the event listener to unregister.
     * @returns
     */
    unregisterOnSceneChange(listenerId) {
        delete this.sceneChangeCallbacks[listenerId];
        let promise;
        if (Object.keys(this.sceneChangeCallbacks).length === 0) {
            promise = this.sendCommand('unregisterOnSceneChange', this.sceneChangeListenerId).then(() => {
                this.sceneChangeListenerId = -1;
                return null;
            });
        }
        else {
            promise = Promise.resolve(null);
        }
        return promise;
    }
    /**
     * Add an item to the context menu in the viewing area.
     * @param text Title of the new context menu item
     * @param callback Callback function triggered by the context menu item. The argument passed to thec allback function is an object containing the x and y coordinates of the right-click which has opened the context menu.
     * @returns
     */
    addContextMenuItem(text, callback) {
        if (typeof callback !== 'function') {
            return Promise.reject('Callback function required');
        }
        if (text in this.contextMenuItemCallbacks) {
            return Promise.reject(`Context menu item "${text}" exists already`);
        }
        this.contextMenuItemCallbacks[text] = callback;
        return this.sendCommand('addContextMenuItem', text);
    }
    /**
     * Get the 3D point for given pixel coordinates on screen.
     * @param pt2 Pixel coordinates on screen
     * @returns The getMousePosition3D() method returns an object containing the coordinates of the 3D point. If no 3D geometry is displayed at the given screen coordinates, the result is null.
     */
    getMousePosition3D(pt2) {
        return this.sendCommand('getMousePosition3D', pt2);
    }
}
//# sourceMappingURL=index.js.map